// @flow
import React from 'react';
import styled from 'styled-components';
import Gandalf from 'gandalf-validator';
import { SidebarSection, TextBlock, Text, Button } from 'bench-internal-theme';
import {
  TextInput,
  ComboBoxInput,
  CurrencyInput,
  type ComboBoxOptions,
} from 'bench-internal-theme/form-input-fields';
import { colors } from 'bench-style-constants';
import { WrappedSpinner } from 'spinner';

import type { ClientRule } from './types';

const ClientRulesLayout = styled.div`
  display: grid;
  grid-template-rows: 1fr min-content;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`;

const ConditionListLayout = styled.div`
  display: grid;
  grid-template-columns: 140px 80px 148px;
  grid-column-gap: 2px;
  grid-row-gap: 4px;
  align-items: center;
`;

const FormFields = styled.div`
  overflow: auto;
`;

const FormActions = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  column-gap: 16px;
  padding: 8px 24px;
  background-color: ${colors.pearl};
`;

const PriorityDescription = styled(TextBlock)`
  padding: 24px 0 8px 0;
`;

const ConditionsDescription = styled(TextBlock)`
  padding-bottom: 32px;
`;

const InlineInputLayout = styled.div`
  display: grid;
  grid-template-columns: 55px 312px;
  align-items: center;
  grid-column-gap: 35px;
`;

type ClientRulesFormGandalfProps = {
  clientRule?: ClientRule,
  dismiss: () => any,
  functionalLedgerOptions: Array<ComboBoxOptions>,
  ledgerAccountOptions: Array<ComboBoxOptions>,
  onSubmit: (clientRuleInput: Object) => any,
};

class ClientRulesForm extends Gandalf<ClientRulesFormGandalfProps> {
  componentDidMount() {
    const { functionalLedgerOptions, ledgerAccountOptions, clientRule } = this.props;

    const clientRuleData = clientRule || {};
    const { vendorContains, minAmount, maxAmount, priority, ledgerAccount, sourceLedgerAccount } =
      clientRuleData;

    const ledgerAccountId = ledgerAccount && ledgerAccount.id;
    const sourceLedgerAccountId = sourceLedgerAccount && sourceLedgerAccount.id;

    const fieldDefinitions = [
      {
        name: 'vendorContains',
        component: TextInput,
        defaultValue: vendorContains,
        validators: ['required'],
        errorPropName: 'errorText',
      },
      {
        name: 'minAmount',
        component: CurrencyInput,
        defaultValue: minAmount,
        getValueInOnChange: changeEvent => changeEvent,
        validators: ['numeric'],
        errorPropName: 'errorText',
        props: {
          defaultValueInCents: minAmount,
        },
      },
      {
        name: 'maxAmount',
        component: CurrencyInput,
        defaultValue: maxAmount,
        getValueInOnChange: changeEvent => changeEvent,
        validators: ['numeric'],
        errorPropName: 'errorText',
        props: {
          defaultValueInCents: maxAmount,
        },
      },
      {
        name: 'priority',
        component: TextInput,
        defaultValue: priority ? priority.toString() : '1',
        validators: ['required', 'numeric', { name: 'min', value: 1 }],
        errorPropName: 'errorText',
        props: {
          type: 'number',
        },
      },
      {
        name: 'ledgerAccountId',
        component: ComboBoxInput,
        validators: ['required'],
        errorPropName: 'errorText',
        defaultValue: ledgerAccountOptions.find((option: any) => option.value === ledgerAccountId),
        getValueInOnChange: changeEvent => changeEvent,
        props: {
          isClearable: true,
          isSearchable: true,
          menuPlacement: 'auto',
          options: ledgerAccountOptions,
        },
      },
      {
        name: 'sourceLedgerAccountId',
        component: ComboBoxInput,
        validators: [],
        defaultValue: functionalLedgerOptions.find(
          (option: any) => option.value === sourceLedgerAccountId,
        ),
        getValueInOnChange: changeEvent => changeEvent,
        props: {
          isClearable: true,
          isSearchable: true,
          menuPlacement: 'auto',
          options: functionalLedgerOptions,
        },
      },
    ];

    this.buildFields(fieldDefinitions);
  }

  handleSubmit(submitEvent: any) {
    submitEvent.preventDefault();
    const formData = this.getCleanFormData();

    if (!formData) {
      return;
    }

    this.props.onSubmit(formData);
  }

  render() {
    const { dismiss } = this.props;
    const { fields } = this.state;

    if (!this.areFieldsReady()) {
      return <WrappedSpinner />;
    }

    return (
      <form
        data-test-id="client-rules-form"
        onSubmit={submitEvent => this.handleSubmit(submitEvent)}
      >
        <ClientRulesLayout>
          <FormFields>
            <SidebarSection title="Conditions" hasBorderBottom>
              <ConditionsDescription styleType="small" color={colors.smoke}>
                When a transaction meets the following conditions, this rule will be applied to it.
                This rule will only be applied when all conditions are met.
              </ConditionsDescription>
              <ConditionListLayout>
                <Text uppercase styleType="small">
                  Field
                </Text>
                <Text uppercase styleType="small">
                  Parameter
                </Text>
                <Text uppercase styleType="small">
                  Value
                </Text>
                <Text styleType="small" color={colors.ash}>
                  Transaction Description
                </Text>
                <Text styleType="small" color={colors.ash}>
                  contains
                </Text>
                {fields.vendorContains.element}
                <Text styleType="small" color={colors.ash}>
                  Account Ledger
                </Text>
                <Text styleType="small" color={colors.ash}>
                  equals to
                </Text>
                {fields.sourceLedgerAccountId.element}
                <Text styleType="small" color={colors.ash}>
                  Amount
                </Text>
                <Text styleType="small" color={colors.ash}>
                  greater than or equal to
                </Text>
                {fields.minAmount.element}
                <Text styleType="small" color={colors.ash}>
                  Amount
                </Text>
                <Text styleType="small" color={colors.ash}>
                  less than or equal to
                </Text>
                {fields.maxAmount.element}
              </ConditionListLayout>
            </SidebarSection>
            <SidebarSection title="Categorization" hasBorderBottom background={colors.pearl}>
              <InlineInputLayout>
                <Text uppercase styleType="small">
                  Move to
                </Text>
                {fields.ledgerAccountId.element}
              </InlineInputLayout>
              <PriorityDescription styleType="small" color={colors.smoke}>
                Choose a number between 1 (highest) and 10 (lowest) to indicate the relative
                priority of this rule compared to other categorization rules with similar
                conditions.
              </PriorityDescription>
              <InlineInputLayout>
                <Text uppercase styleType="small">
                  Priority
                </Text>
                {fields.priority.element}
              </InlineInputLayout>
            </SidebarSection>
          </FormFields>
          <FormActions>
            <Button
              data-test-id="client-rules-form-cancel"
              size="large"
              styleType="ghost"
              type="button"
              onClick={dismiss}
            >
              Cancel
            </Button>
            <Button size="large" styleType="primary" type="submit">
              Save
            </Button>
          </FormActions>
        </ClientRulesLayout>
      </form>
    );
  }
}

export default ClientRulesForm;
